"use client";

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface AiDotsProps {
  className?: string;
  /** Term screens to show (split into two). Defaults to general AI buzzwords.
   *  Use generic, non-trademarked terms only. */
  words?: readonly string[];
}

interface Pt {
  x: number;
  y: number;
}

/** Generic, non-trademarked AI buzzwords — two screens' worth, shown as dot text. */
const BUZZWORDS_A = [
  "LLM", "PROMPTING", "AGENTS", "RAG", "TOKENS", "AUTOMATION",
  "CHATBOTS", "GENERATIVE AI", "NEURAL NETS", "ALGORITHMS", "ASSISTANTS", "MODELS",
];
const BUZZWORDS_B = [
  "EMBEDDINGS", "FINE-TUNING", "INFERENCE", "MULTIMODAL", "GUARDRAILS", "WORKFLOWS",
  "VECTORS", "DEEP LEARNING", "CONTEXT", "DATASETS", "HALLUCINATION", "CLASSIFIERS",
];

/**
 * A looping, AI-themed particle animation: dots assemble into the word "AI",
 * then into two screens of AI terms, then loop back. Pauses when off-screen or
 * the tab is hidden; respects prefers-reduced-motion (renders the "AI" wordmark
 * once, static). Decorative.
 */
export function AiDots({ className, words }: AiDotsProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const css = getComputedStyle(document.documentElement);
    const moss = css.getPropertyValue("--color-primary").trim() || "#1e3a2b";
    const brass = css.getPropertyValue("--color-secondary").trim() || "#8a5616";

    const N = 5200;
    const MORPH = 1100;
    const HOLD = 2400;
    const PAD = 0.1; // fraction of the canvas kept clear around the artwork

    let W = 0;
    let H = 0;
    let lastW = -1;

    let frames: Pt[][] = [];
    let positions: Pt[] = [];
    let from: Pt[] = [];
    let idx = 0;
    let phase: "morph" | "hold" = "hold";
    let phaseStart = 0;
    let raf = 0;
    let visible = true;

    const rand = () => Math.random();

    function sampleText(text: string): Pt[] {
      const oc = document.createElement("canvas");
      oc.width = W;
      oc.height = H;
      const o = oc.getContext("2d");
      if (!o) return [];
      const innerW = W * (1 - 2 * PAD);
      const innerH = H * (1 - 2 * PAD);
      const fs = Math.min(innerW / (text.length * 0.6), innerH / 0.72);
      o.font = `800 ${fs}px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif`;
      o.textAlign = "center";
      o.textBaseline = "middle";
      o.fillStyle = "#000";
      o.fillText(text, W / 2, H / 2);
      const d = o.getImageData(0, 0, W, H).data;
      const gap = Math.max(4, Math.round(fs / 15));
      const pts: Pt[] = [];
      for (let y = 0; y < H; y += gap) {
        for (let x = 0; x < W; x += gap) {
          if (d[(y * W + x) * 4 + 3] > 130) pts.push({ x, y });
        }
      }
      return pts;
    }

    function sampleWords(words: string[]): Pt[] {
      const oc = document.createElement("canvas");
      oc.width = W;
      oc.height = H;
      const o = oc.getContext("2d");
      if (!o) return [];
      const insetY = H * PAD;
      const innerW = W * (1 - 2 * PAD);
      const innerH = H * (1 - 2 * PAD);
      const rows = 6;
      const rowH = innerH / rows;
      const fs = rowH * 0.72;
      o.font = `800 ${fs}px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif`;
      o.textBaseline = "middle";
      o.textAlign = "center";
      o.fillStyle = "#000";
      const sep = ", ";
      const sepW = o.measureText(sep).width;
      let wi = 0;
      for (let r = 0; r < rows; r++) {
        const rowWords: string[] = [];
        let rowW = 0;
        let guard = 0;
        while (guard++ < 40) {
          const w = words[wi % words.length];
          const ww = o.measureText(w).width;
          const add = (rowWords.length ? sepW : 0) + ww;
          if (rowWords.length && rowW + add > innerW) break;
          rowWords.push(w);
          rowW += add;
          wi++;
          if (rowW > innerW * 0.78) break;
        }
        const y = insetY + r * rowH + rowH / 2;
        const line = rowWords.join(", ") + (r < rows - 1 ? "," : "");
        o.fillText(line, W / 2, y);
      }
      const d = o.getImageData(0, 0, W, H).data;
      const gap = Math.max(2, Math.round(fs / 18));
      const pts: Pt[] = [];
      for (let y = 0; y < H; y += gap) {
        for (let x = 0; x < W; x += gap) {
          if (d[(y * W + x) * 4 + 3] > 130) pts.push({ x, y });
        }
      }
      return pts;
    }

    function resampleTo(points: Pt[], n: number): Pt[] {
      if (points.length === 0) {
        return Array.from({ length: n }, () => ({ x: W / 2, y: H / 2 }));
      }
      const order = points.map((_, i) => i);
      for (let i = order.length - 1; i > 0; i--) {
        const j = (rand() * (i + 1)) | 0;
        [order[i], order[j]] = [order[j], order[i]];
      }
      const out: Pt[] = [];
      for (let i = 0; i < n; i++) {
        const p = points[order[i % points.length]];
        const dup = i >= points.length;
        out.push({
          x: p.x + (dup ? (rand() - 0.5) * 4 * DPR : 0),
          y: p.y + (dup ? (rand() - 0.5) * 4 * DPR : 0),
        });
      }
      return out;
    }

    function build() {
      const w = wrap!.clientWidth;
      const h = wrap!.clientHeight;
      W = Math.round(w * DPR);
      H = Math.round(h * DPR);
      canvas!.width = W;
      canvas!.height = H;
      // Display size must stay fluid (100%), not a fixed px width: a fixed px
      // canvas sets a min-content floor that stops the grid column shrinking
      // below it, overflowing the hero on mobile. The buffer (W×H) keeps it crisp.
      canvas!.style.width = "100%";
      canvas!.style.height = "100%";
      lastW = w;

      const pool = words && words.length ? words : null;
      const screenA = pool ? pool.slice(0, Math.ceil(pool.length / 2)) : BUZZWORDS_A;
      const screenB = pool ? pool.slice(Math.ceil(pool.length / 2)) : BUZZWORDS_B;
      frames = [
        resampleTo(sampleText("AI"), N),
        resampleTo(sampleWords(screenA), N),
        resampleTo(sampleWords(screenB), N),
      ];
      positions = frames[0].map((p) => ({ x: p.x, y: p.y }));
      from = positions.map((p) => ({ ...p }));
      idx = 0;
      phase = "hold";
      phaseStart = performance.now();
    }

    function render(now: number) {
      if (!ctx) return;
      ctx.clearRect(0, 0, W, H);
      const cur = frames[idx];
      const t = now - phaseStart;

      if (!reduced) {
        if (phase === "morph") {
          const k = Math.min(1, t / MORPH);
          const e = 1 - Math.pow(1 - k, 3);
          for (let i = 0; i < N; i++) {
            positions[i].x = from[i].x + (cur[i].x - from[i].x) * e;
            positions[i].y = from[i].y + (cur[i].y - from[i].y) * e;
          }
          if (k >= 1) {
            phase = "hold";
            phaseStart = now;
          }
        } else {
          for (let i = 0; i < N; i++) {
            positions[i].x = cur[i].x + Math.sin(now * 0.0011 + i) * 0.7 * DPR;
            positions[i].y = cur[i].y + Math.cos(now * 0.0013 + i * 1.3) * 0.7 * DPR;
          }
          if (t > HOLD) {
            idx = (idx + 1) % frames.length;
            from = positions.map((p) => ({ ...p }));
            phase = "morph";
            phaseStart = now;
          }
        }
      }

      const r = Math.max(1, DPR * 0.8);
      for (let i = 0; i < N; i++) {
        ctx.beginPath();
        ctx.arc(positions[i].x, positions[i].y, r, 0, Math.PI * 2);
        ctx.fillStyle = i % 8 === 0 ? brass : moss;
        ctx.fill();
      }

      if (!reduced && visible) raf = requestAnimationFrame(render);
    }

    function start() {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(render);
    }

    build();
    if (reduced) {
      idx = 0; // show the "AI" wordmark, static
      positions = frames[0].map((p) => ({ x: p.x, y: p.y }));
      render(performance.now());
    } else {
      start();
    }

    // Pause when off-screen.
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          visible = e.isIntersecting;
          if (visible && !reduced) start();
          else cancelAnimationFrame(raf);
        }
      },
      { threshold: 0.05 },
    );
    io.observe(wrap);

    // Resume when tab becomes visible.
    const onVis = () => {
      if (!document.hidden && visible && !reduced) start();
    };
    document.addEventListener("visibilitychange", onVis);

    let rzTimer = 0;
    const ro = new ResizeObserver(() => {
      if (Math.abs(wrap!.clientWidth - lastW) < 8) return;
      window.clearTimeout(rzTimer);
      rzTimer = window.setTimeout(() => {
        build();
        if (reduced) render(performance.now());
        else if (visible) start();
      }, 180);
    });
    ro.observe(wrap);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      ro.disconnect();
      window.clearTimeout(rzTimer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [words]);

  return (
    <div ref={wrapRef} aria-hidden="true" className={cn("w-full", className)}>
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}
